'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import gsap from 'gsap';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { usePageStore } from '@/store/usePageStore';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { useIsNotTouchDevice } from '@/hooks/useIsNotTouchDevice';
import { localizedSlugs } from '@/i18n/routing';

gsap.registerPlugin(ScrollSmoother, ScrollTrigger);

export default function GsapScrollSmoother() {
  // Keep smoother instance local to this component
  const smootherRef = useRef<ScrollSmoother | null>(null);

  const pathname = usePathname();
  const reducedMotion = usePrefersReducedMotion();

  // Keep the callback (sets ready=true) but don't expose smoother globally
  const isNotTouchDevice = useIsNotTouchDevice(() => {
    usePageStore.getState().setIsSmootherReady(true);
  });

  /* Firefox SecurityError in x-origin iframes — ignore */  
  const scrollTriggerRefresh = () => {
    try { ScrollTrigger.refresh(); } catch {} 
  };

  // Memoize store fns you still need (no smoother instance setter anymore)
  const setIsSmootherReady = useRef(usePageStore.getState().setIsSmootherReady);
  const setHeightWindowUnit = useRef(usePageStore.getState().setHeightWindowUnit);
  const setUserPrefersReducedMotion = useRef(usePageStore.getState().setUserPrefersReducedMotion);
  const isPreviousPageWork = useRef(usePageStore.getState().isPreviousPageWork);

  const isScrollBlocked = usePageStore((s) => s.isScrollBlocked);
  const savedScrollY = useRef(0);

  useEffect(() => {
    // update vh unit and reduced motion pref in store (lightweight)
    setHeightWindowUnit.current(window.innerHeight * 0.01);
    setUserPrefersReducedMotion.current(reducedMotion);

    const wrapper = document.querySelector('#smooth-wrapper');
    const content = document.querySelector('#smooth-content');

    const localSetIsSmootherReady = setIsSmootherReady.current;
    const localSmootherRef = smootherRef;

    const initSmoother = () => {
      if (!wrapper || !content) {
        // Wait until the layout exists
        requestAnimationFrame(initSmoother);
        return;
      }

      // Kill any previous instance before creating a new one
      if (localSmootherRef.current) {
        localSmootherRef.current.kill();
        localSmootherRef.current = null;
      }

      if (isNotTouchDevice) {
        const smoother = ScrollSmoother.create({
          smooth: 0.8,
          effects: true,
          normalizeScroll: true,
          wrapper: '#smooth-wrapper',
          content: '#smooth-content',
        });

        localSmootherRef.current = smoother;

        requestAnimationFrame(() => {
          localSetIsSmootherReady(true);

          const slugWork = localizedSlugs.work.en || localizedSlugs.work.es;
          const shouldScrollTop = !(isPreviousPageWork.current && pathname.includes(slugWork));

          if (shouldScrollTop) {
            smoother.scrollTo(0, false);
          }

          // Let ScrollTrigger measure after DOM settles
          setTimeout(() => {
            scrollTriggerRefresh();
          }, 0);
        });
      } else {
        setTimeout(() => {
          scrollTriggerRefresh();
        }, 0);
      }
    };

    initSmoother();

    return () => {
      // Reset ready flag
      localSetIsSmootherReady(false);

      // Kill all triggers and the local smoother to avoid leaks
      ScrollTrigger.getAll().forEach((t) => t.kill());

      if (localSmootherRef.current) {
        localSmootherRef.current.kill();
        localSmootherRef.current = null;
      }
    };
  }, [pathname, reducedMotion, isNotTouchDevice]);

  const lastPathnameRef = useRef(pathname);
  const skipRestoreOnUnblockRef = useRef(false);

  useEffect(() => {
    // If we are blocked and the route changed, mark that we should skip restoring.
    if (isScrollBlocked && pathname !== lastPathnameRef.current) {
      skipRestoreOnUnblockRef.current = true;
    }
    lastPathnameRef.current = pathname;
  }, [pathname, isScrollBlocked]);

  useEffect(() => {
    const smoother = smootherRef.current;

    if (smoother) {
      smoother.paused(isScrollBlocked);
      return;
    }

    const body = document.body;
    const html = document.documentElement;

    if (isScrollBlocked) {
      // remember position and freeze body
      savedScrollY.current = window.scrollY || window.pageYOffset || 0;
      body.style.position = 'fixed';
      body.style.top = `-${savedScrollY.current}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      body.style.touchAction = 'none';
      html.style.overscrollBehavior = 'none';
    } else {
      // unfreeze
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      body.style.touchAction = '';
      html.style.overscrollBehavior = '';

      // if we navigated while blocked -> go to top and don't restore
      if (skipRestoreOnUnblockRef.current) {
        skipRestoreOnUnblockRef.current = false;
        savedScrollY.current = 0; // reset the stale value
        window.scrollTo(0, 0);
      } else {
        // closed menu without navigating -> restore
        window.scrollTo(0, savedScrollY.current || 0);
      }
    }

    return () => {
      // safety unlock
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      body.style.touchAction = '';
      html.style.overscrollBehavior = '';
    };
  }, [isScrollBlocked]);

  return null;
}
